import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckCircle, XCircle, Clock, AlertCircle, ArrowLeft, ChevronDown, ChevronRight, Smartphone, GitBranch, GitCommit, Image, X } from 'lucide-react';
import clsx from 'clsx';
import { apiService } from '../services/api';
import JiraTicketAction from '../components/JiraTicketAction';

const MobileTestRunDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [run, setRun] = useState(null);
  const [journeys, setJourneys] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | failed | passed
  const [expandedSuites, setExpandedSuites] = useState({});
  const [screenshotModal, setScreenshotModal] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showPassing, setShowPassing] = useState({});

  useEffect(() => {
    apiService.getMobileTestRun(id)
      .then((res) => setRun(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
    apiService.getMobileTestRunJourneys(id)
      .then((res) => setJourneys(res.data))
      .catch(() => setJourneys(null));
  }, [id]);

  // Auto-open category from ?category= query param
  useEffect(() => {
    const queryCat = searchParams.get('category');
    if (queryCat && journeys && journeys[queryCat] && !activeCategory) {
      setActiveCategory(queryCat);
    }
  }, [journeys, searchParams, activeCategory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  const passed = run.total_tests - run.total_failures;
  const hasFailed = run.total_failures > 0;
  const passRate = run.total_tests > 0 ? (passed / run.total_tests * 100).toFixed(1) : 0;

  // Count test cases by status across all suites
  const allTestCases = run.test_suites.flatMap((s) => s.testcases);
  const counts = {
    all: allTestCases.length,
    passed: allTestCases.filter((tc) => tc.status === 'SUCCESS').length,
    failed: allTestCases.filter((tc) => tc.status === 'FAILURE').length,
    skipped: allTestCases.filter((tc) => tc.status === 'SKIPPED').length,
  };

  // Filter suites based on selected filter
  const filteredSuites = run.test_suites.map((suite) => {
    if (filter === 'all') return suite;
    const statusMap = { passed: 'SUCCESS', failed: 'FAILURE', skipped: 'SKIPPED' };
    const filtered = suite.testcases.filter((tc) => tc.status === statusMap[filter]);
    if (filtered.length === 0) return null;
    return { ...suite, testcases: filtered };
  }).filter(Boolean);

  const toggleSuite = (idx) => {
    setExpandedSuites((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <div className="space-y-6">
      {/* Back link + header */}
      <div>
        <Link to="/test-runs" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft className="h-4 w-4" /> Back to Test Runs
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Smartphone className="h-6 w-6 text-primary-600" />
              Mobile Run #{run.id}
            </h1>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="inline-flex items-center text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">
                {run.project}
              </span>
              <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                <GitBranch className="h-3 w-3 mr-1" />{run.branch}
              </span>
              <span className="inline-flex items-center text-xs text-gray-400 font-mono">
                <GitCommit className="h-3 w-3 mr-1" />{run.commit_sha.substring(0, 7)}
              </span>
              <span className="text-xs text-gray-400">
                Pipeline #{run.pipeline_id}
              </span>
            </div>
          </div>
          <div className="text-right">
            <span className={clsx(
              'inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm font-medium capitalize',
              hasFailed
                ? 'bg-error-50 text-error-700 border-error-200'
                : 'bg-success-50 text-success-700 border-success-200'
            )}>
              {hasFailed ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
              {hasFailed ? 'Failed' : 'Passed'}
            </span>
            <p className="text-xs text-gray-400 mt-1">
              {format(new Date(run.timestamp), 'MMM dd, yyyy HH:mm')}
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Total Tests" value={run.total_tests} />
        <Stat label="Passed" value={passed} color="text-success-600" />
        <Stat label="Failed" value={run.total_failures} color="text-error-600" />
        <Stat label="Duration" value={`${run.total_time_seconds.toFixed(1)}s`} />
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-medium text-gray-700">Pass Rate: {passRate}%</span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden flex">
          {passed > 0 && (
            <div
              className="bg-success-500 h-full"
              style={{ width: `${(passed / run.total_tests) * 100}%` }}
            />
          )}
          {run.total_failures > 0 && (
            <div
              className="bg-error-500 h-full"
              style={{ width: `${(run.total_failures / run.total_tests) * 100}%` }}
            />
          )}
        </div>
      </div>

      {/* Director-friendly journey breakdown */}
      {journeys && (() => {
        const catData = Object.entries(journeys).map(([cat, jmap]) => {
          const journeyArr = Object.entries(jmap).map(([n, j]) => ({ name: n, ...j }));
          const cPassed = journeyArr.reduce((a, j) => a + j.passed, 0);
          const cFailed = journeyArr.reduce((a, j) => a + j.failed, 0);
          const cSkipped = journeyArr.reduce((a, j) => a + j.skipped, 0);
          const cTotal = cPassed + cFailed + cSkipped;
          const hasRed = journeyArr.some((j) => j.status === 'red');
          const hasYellow = journeyArr.some((j) => j.status === 'yellow');
          const hasGreen = journeyArr.some((j) => j.status === 'green');
          let cStatus = 'grey';
          if (hasRed) cStatus = 'red';
          else if (hasYellow) cStatus = 'yellow';
          else if (hasGreen) cStatus = 'green';
          return { cat, journeys: journeyArr, passed: cPassed, failed: cFailed, skipped: cSkipped, total: cTotal, status: cStatus };
        }).filter((c) => c.total > 0);

        const failureLines = [];
        catData.forEach((c) => {
          c.journeys.forEach((j) => {
            (j.failed_tests || []).forEach((t) => {
              failureLines.push({
                cat: c.cat,
                journey: j.name,
                title: t.title,
                suite: t.suite,
                summary: t.summary,
                errorFriendly: t.error_friendly,
                screenshots: t.screenshots || [],
              });
            });
          });
        });

        return (
          <div className="space-y-5">
            {failureLines.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">
                  Failure summary <span className="text-gray-400 font-normal">({failureLines.length} failed)</span>
                </h2>
                <ul className="space-y-2.5 max-h-[28rem] overflow-y-auto pr-1">
                  {failureLines.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-error-500 shrink-0 mt-2" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-gray-700">
                          <span className="font-medium text-error-700">{f.title}</span>
                          <span className="text-gray-500"> — from </span>
                          <span className="font-medium text-gray-800">{f.journey}</span>
                          <span className="text-gray-500"> under </span>
                          <span className="font-medium text-gray-800">{f.cat}</span>
                        </div>
                        {f.summary && (
                          <div className="text-xs text-gray-600 mt-1 leading-snug">
                            <span className="font-medium text-gray-500">What this test does: </span>
                            {f.summary}
                          </div>
                        )}
                        {f.errorFriendly && (
                          <div className="text-xs text-error-700 mt-0.5">{f.errorFriendly}</div>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                          {f.screenshots[0] && (
                            <button
                              onClick={() => setScreenshotModal(f.screenshots[0])}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-error-300 bg-white text-error-700 hover:bg-error-50 transition-colors"
                            >
                              <Image className="h-3 w-3" /> View screenshot
                            </button>
                          )}
                          {f.suite && (
                            <JiraTicketAction
                              cacheKey={`mobile-${run.id}-${f.suite}-${f.title}`}
                              fetchSuggestion={() => apiService.getMobileJiraSuggestion(run.id, f.suite, f.title)}
                              createTicket={() => apiService.createMobileJiraTicket(run.id, f.suite, f.title)}
                            />
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="bg-success-50 border border-success-200 rounded-xl p-5 text-center">
                <CheckCircle className="h-6 w-6 text-success-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-success-700">All systems healthy — no failures in this run</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {catData.map((c) => (
                <CategoryCard
                  key={c.cat}
                  category={c.cat}
                  passed={c.passed}
                  failed={c.failed}
                  skipped={c.skipped}
                  total={c.total}
                  status={c.status}
                  active={activeCategory === c.cat}
                  onClick={() => setActiveCategory(activeCategory === c.cat ? null : c.cat)}
                />
              ))}
            </div>

            {activeCategory && (() => {
              const active = catData.find((c) => c.cat === activeCategory);
              if (!active) return null;
              const visible = active.journeys.filter((j) => j.passed + j.failed + j.skipped > 0);
              const theme = STATUS_THEME[active.status] || STATUS_THEME.grey;
              return (
                <div className={clsx('rounded-xl border-2 bg-white overflow-hidden', theme.border)}>
                  <div className={clsx('px-5 py-4 border-b flex items-center justify-between', theme.border, theme.bg)}>
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-bold text-gray-900">
                        {active.cat} — Categorical Breakdown
                      </h2>
                      <span className={clsx('text-[11px] font-semibold px-2.5 py-0.5 rounded-full tracking-wide', theme.strong, theme.text)}>
                        {theme.label}
                      </span>
                    </div>
                    <button
                      onClick={() => setActiveCategory(null)}
                      className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                    >
                      <X className="h-3.5 w-3.5" /> Close
                    </button>
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-gray-500 mb-4">
                      Each card below is one section of {active.cat}. Red = something broken, yellow = some failures, green = healthy.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {visible.map((j) => (
                        <JourneyCard
                          key={j.name}
                          name={j.name}
                          data={j}
                          onScreenshot={(url) => setScreenshotModal(url)}
                          showPassing={!!showPassing[`${active.cat}|${j.name}`]}
                          togglePassing={() => setShowPassing((prev) => ({
                            ...prev,
                            [`${active.cat}|${j.name}`]: !prev[`${active.cat}|${j.name}`],
                          }))}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Detailed suite list (raw view) */}
      <div className="pt-2">
        <h2 className="text-sm font-medium text-gray-700 mb-3">Detailed suite results</h2>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {['all', 'failed', 'passed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={clsx(
              'px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors',
              filter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {f} ({counts[f] || 0})
          </button>
        ))}
      </div>

      {/* Test Suites */}
      <div className="space-y-3">
        {filteredSuites.map((suite, idx) => {
          const suiteFailed = suite.failures > 0 || suite.testcases.some((tc) => tc.status === 'FAILURE');
          const isOpen = expandedSuites[idx] !== undefined ? expandedSuites[idx] : suiteFailed;

          return (
            <div
              key={idx}
              className={clsx('bg-white rounded-xl border overflow-hidden', suiteFailed ? 'border-error-200' : 'border-gray-200')}
            >
              {/* Suite header */}
              <button
                onClick={() => toggleSuite(idx)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  {suiteFailed ? (
                    <XCircle className="h-4 w-4 text-error-500" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-success-500" />
                  )}
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-800">{suite.name}</p>
                    <p className="text-xs text-gray-400">{suite.device}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={clsx(
                    'text-xs px-2 py-0.5 rounded-full',
                    suiteFailed ? 'bg-error-50 text-error-700' : 'bg-success-50 text-success-700'
                  )}>
                    {suite.testcases.length} tests{suite.failures > 0 ? `, ${suite.failures} failed` : ''}
                  </span>
                  <span className="text-xs text-gray-400">{suite.time_seconds.toFixed(1)}s</span>
                </div>
              </button>

              {/* Test cases */}
              {isOpen && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {suite.testcases.map((tc, tcIdx) => (
                    <div key={tcIdx} className="flex items-start px-5 py-2.5 hover:bg-gray-50/50">
                      <div className="mt-0.5 mr-3">
                        {tc.status === 'SUCCESS' ? (
                          <CheckCircle className="h-4 w-4 text-success-500" />
                        ) : tc.status === 'FAILURE' ? (
                          <XCircle className="h-4 w-4 text-error-500" />
                        ) : (
                          <Clock className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800">{tc.name}</p>
                        {tc.failure_message && (
                          <pre className="mt-1 text-xs text-error-700 bg-error-50 border border-error-100 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                            {tc.failure_message}
                          </pre>
                        )}
                        {tc.screenshots && tc.screenshots.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {tc.screenshots.map((url, sIdx) => (
                              <button
                                key={sIdx}
                                onClick={() => setScreenshotModal(url)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                              >
                                <Image className="h-3.5 w-3.5" />
                                Screenshot {tc.screenshots.length > 1 ? sIdx + 1 : ''}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 ml-3 flex-shrink-0">
                        {tc.time_seconds.toFixed(1)}s
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {filteredSuites.length === 0 && (
          <div className="text-center py-12">
            <CheckCircle className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No test cases match the selected filter</p>
          </div>
        )}
      </div>

      {/* Screenshot Modal */}
      {screenshotModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setScreenshotModal(null)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setScreenshotModal(null)}
              className="absolute -top-10 right-0 p-1 text-white/80 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src={screenshotModal}
              alt="Screenshot"
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

function Stat({ label, value, color = 'text-gray-900' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
      <p className={clsx('text-xl font-bold', color)}>{value}</p>
    </div>
  );
}

const STATUS_THEME = {
  green:  { bg: 'bg-success-50', border: 'border-success-300', strong: 'bg-success-100', text: 'text-success-700', label: 'OK' },
  yellow: { bg: 'bg-warning-50', border: 'border-warning-300', strong: 'bg-warning-100', text: 'text-warning-700', label: 'WARNING' },
  red:    { bg: 'bg-error-50',   border: 'border-error-300',   strong: 'bg-error-100',   text: 'text-error-700',   label: 'CRITICAL' },
  grey:   { bg: 'bg-gray-50',    border: 'border-gray-200',    strong: 'bg-gray-100',    text: 'text-gray-500',    label: 'NO TESTS' },
};

function CategoryCard({ category, passed, failed, skipped, total, status, active, onClick }) {
  const theme = STATUS_THEME[status] || STATUS_THEME.grey;
  const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left rounded-xl border-2 p-5 transition-all',
        theme.bg,
        active ? theme.border : 'border-transparent',
        'hover:shadow-md'
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h2 className="text-lg font-bold text-gray-900">{category}</h2>
        <span className={clsx('text-[10px] font-semibold px-2 py-0.5 rounded-full tracking-wide shrink-0', theme.strong, theme.text)}>
          {theme.label}
        </span>
      </div>
      <div className="space-y-1">
        <div className="flex items-baseline gap-2">
          <span className={clsx('text-3xl font-bold', theme.text)}>{passRate}%</span>
          <span className="text-xs text-gray-500">pass rate</span>
        </div>
        <div className="text-xs text-gray-600 flex items-center gap-3 flex-wrap">
          <span className="text-success-700">{passed} passed</span>
          {failed > 0 && <span className="text-error-700 font-medium">{failed} failed</span>}
          {skipped > 0 && <span className="text-gray-500">{skipped} skipped</span>}
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          {active ? 'Click to collapse' : 'Click to drill down'}
        </p>
      </div>
    </button>
  );
}

function FailedTestCard({ test, onScreenshot }) {
  const [showRaw, setShowRaw] = useState(false);
  const screenshot = test.screenshots && test.screenshots[0];
  return (
    <div className="bg-white rounded-lg border border-error-200 p-2.5">
      <div className="flex items-start gap-1.5 min-w-0">
        <XCircle className="h-3.5 w-3.5 text-error-500 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-800">{test.title}</p>
          {test.summary && (
            <p className="text-[11px] text-gray-600 mt-0.5 leading-snug">{test.summary}</p>
          )}
          {test.error_friendly && (
            <p className="text-[11px] text-error-700 mt-0.5">{test.error_friendly}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {screenshot && (
              <button
                onClick={() => onScreenshot(screenshot)}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-error-300 bg-white text-error-700 hover:bg-error-50 transition-colors"
              >
                <Image className="h-3 w-3" /> View screenshot
              </button>
            )}
            {test.error_raw && (
              <button
                onClick={() => setShowRaw((v) => !v)}
                className="text-[10px] text-gray-500 hover:text-gray-700 underline"
              >
                {showRaw ? 'Hide technical details' : 'Show technical details'}
              </button>
            )}
          </div>
          {showRaw && test.error_raw && (
            <pre className="mt-1.5 text-[10px] text-gray-700 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
              {test.error_raw}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function PassingTestRow({ test, onScreenshot }) {
  const [open, setOpen] = useState(false);
  const hasSummary = !!test.summary;
  const screenshot = test.screenshots && test.screenshots[0];
  return (
    <li className="text-[11px] text-gray-600">
      <div className="flex items-start gap-1.5">
        <CheckCircle className="h-3 w-3 text-success-500 mt-0.5 shrink-0" />
        {hasSummary ? (
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex items-start gap-1 flex-1 min-w-0 text-left hover:text-gray-900"
            title={open ? 'Hide summary' : 'Show what this test does'}
          >
            {open
              ? <ChevronDown className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />
              : <ChevronRight className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />}
            <span className={open ? 'flex-1' : 'truncate flex-1'}>{test.title}</span>
          </button>
        ) : (
          <span className="truncate flex-1">{test.title}</span>
        )}
        {screenshot && (
          <button
            onClick={() => onScreenshot(screenshot)}
            className="p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0"
            title="View screenshot"
          >
            <Image className="h-3 w-3" />
          </button>
        )}
      </div>
      {hasSummary && open && (
        <p className="ml-[1.125rem] mt-0.5 mb-1 text-[11px] text-gray-500 leading-snug">{test.summary}</p>
      )}
    </li>
  );
}

function JourneyCard({ name, data, onScreenshot, showPassing, togglePassing }) {
  const theme = STATUS_THEME[data.status] || STATUS_THEME.grey;
  const total = data.passed + data.failed + data.skipped;
  return (
    <div className={clsx('rounded-xl border p-4', theme.bg, theme.border)}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{name}</h3>
        <span className={clsx('text-[10px] font-semibold px-2 py-0.5 rounded-full tracking-wide shrink-0', theme.strong, theme.text)}>
          {theme.label}
        </span>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        {data.failed > 0
          ? <><span className="font-medium text-error-700">{data.failed} failed</span> of {total} tests</>
          : <>{data.passed} of {total} tests passing{data.skipped ? `, ${data.skipped} skipped` : ''}</>}
      </p>

      {data.failed_tests && data.failed_tests.length > 0 && (
        <div className="space-y-2 mb-3">
          {data.failed_tests.map((t, i) => (
            <FailedTestCard key={i} test={t} onScreenshot={onScreenshot} />
          ))}
        </div>
      )}

      {data.skipped_tests && data.skipped_tests.length > 0 && (
        <ul className="space-y-1 mb-3">
          {data.skipped_tests.map((t, i) => (
            <li key={i} className="text-[11px] text-gray-500 flex items-start gap-1.5">
              <Clock className="h-3 w-3 mt-0.5 shrink-0" />
              <span className="truncate">{t.title} <span className="text-gray-400">(skipped)</span></span>
            </li>
          ))}
        </ul>
      )}

      {data.passed > 0 && (
        <div>
          <button
            onClick={togglePassing}
            className={clsx('text-[11px] font-medium', theme.text, 'hover:underline')}
          >
            {showPassing ? 'Hide' : 'Show'} {data.passed} passing test{data.passed === 1 ? '' : 's'}
          </button>
          {showPassing && (
            <ul className="mt-2 space-y-1">
              {data.passed_tests.map((t, i) => (
                <PassingTestRow key={i} test={t} onScreenshot={onScreenshot} />
              ))}
            </ul>
          )}
        </div>
      )}

      {data.failed === 0 && data.skipped === 0 && (
        <p className={clsx('text-xs italic', theme.text)}>All {data.passed} tests passing</p>
      )}
    </div>
  );
}

export default MobileTestRunDetail;
